import type { DomainLead } from '../../../services/leadGenApi';

// LinkedIn-Such-Helfer für den Setter-Kernworkflow: Firma finden + Entscheider finden.
// Alles READ-ONLY (öffnet LinkedIn/Google in neuem Tab) · keine VPS-Schreibvorgänge.

// Default-Entscheider-Rollen, falls keine vertical-spezifischen aus getTargetRoles vorliegen.
export const DEFAULT_ROLES = ['Geschäftsführer', 'Marketing', 'CMO', 'Head of Marketing', 'E-Commerce'];

// Der globale Ziel-Positionen-Override lag bis 2026-07-15 hier in localStorage (getTargetRolesOverride/
// setTargetRolesOverride) · also PRO BROWSER: jeder Setter suchte nach anderen Titeln, unsichtbar für
// alle anderen. Er ist jetzt `target_roles` im lead_distribution-Blob (Server) und erreicht den Stapel
// als Prop über Fraud0Leads. Die localStorage-Helfer sind ersatzlos entfernt.

// Subsidiary-Klartextname (eigene Firma): Impressum bevorzugt, sonst Domain-Marke ohne TLD.
// IGNORIERT bewusst die Mutterfirma · für die NorthData-Recherche, die auf der Tochter ansetzt.
export function subsidiaryName(lead: DomainLead): string {
  if (lead.impressum_json?.company_name) return lead.impressum_json.company_name;
  return lead.domain.replace(/\.(de|com|net|org|io|eu|at|ch|co|shop|de\.com)$/i, '');
}

// Firmen-Klartextname für die LinkedIn-Suche: Mutter-/Holdingfirma bevorzugt (Setter-Override aus
// NorthData), sonst die eigene Firma. So zielen companyLinkedInUrl/peopleXrayUrl/personXrayUrl
// automatisch auf die Mutter, sobald eine Tochterfirma aufgelöst wurde.
// Behält die VOLLE Rechtsform (für NorthData/Handelsregister). Für LinkedIn-Queries: brandName().
export function companyName(lead: DomainLead): string {
  if (lead.parent_company_name) return lead.parent_company_name;
  return subsidiaryName(lead);
}

const _TLD_RE = /\.(de|com|net|org|io|eu|at|ch|co|shop|app|dev|ai|gmbh|berlin|de\.com)$/i;
// "(haftungsbeschränkt)"-Zusatz zuerst entfernen (das \b nach ")" würde sonst nicht greifen).
const _HAFTUNG_RE = /\(?\s*haftungsbeschränkt\s*\)?/gi;
// Rechtsform-Suffixe (mehrteilige zuerst, damit "GmbH & Co. KG" ganz getilgt wird).
const _LEGAL_SUFFIX_RE =
  /\b(gmbh\s*&\s*co\.?\s*kgaa|gmbh\s*&\s*co\.?\s*kg|co\.?\s*kg|kgaa|ggmbh|gmbh|mbh|ug|ag|se|kg|ohg|gbr|e\.?\s*k\.?|e\.?\s*v\.?|ltd\.?|inc\.?|llc|plc)\b/gi;
// Zu generische Ein-Wort-Marken nach dem Strippen → lieber Domain-Root nehmen.
const _GENERIC_BRAND = new Set([
  'media', 'digital', 'group', 'holding', 'online', 'shop', 'store', 'web', 'consulting',
  'marketing', 'solutions', 'systems', 'system', 'tech', 'data', 'services', 'service',
  'company', 'gmbh', 'agentur', 'agency', 'commerce', 'trade', 'handel', 'vertrieb',
]);

// Zweigniederlassungs-/Filial-Präfixe: eine deutsche Niederlassung einer (Auslands-)Firma heißt im
// Impressum z.B. "Niederlassung der Honda Motor Europe Ltd." → das ist keine Marke, sondern eine
// Beschreibung. Weg damit, sonst wird die exakte Phrasensuche zu lang → 0 Treffer.
const _BRANCH_PREFIX_RE =
  /^\s*(?:deutsche\s+)?(?:zweig)?niederlassung\s+(?:der|des|von)\s+|^\s*(?:filiale|repr[äa]sentanz|zweigstelle|betriebsst[äa]tte|geschäftsstelle)\s+(?:der|des|von)\s+/i;

// Rechtsform aus einem Firmennamen entfernen und einen LESBAREN Markennamen zurückgeben
// (nicht den normalisierten Vergleichs-Key). "GoLeasy GmbH" → "GoLeasy",
// "Muster GmbH & Co. KG" → "Muster", "Beispiel UG (haftungsbeschränkt)" → "Beispiel".
export function stripLegalSuffix(name: string): string {
  let s = name.replace(_BRANCH_PREFIX_RE, ' ').replace(_HAFTUNG_RE, ' ').replace(_LEGAL_SUFFIX_RE, ' ');
  s = s.replace(/[&.,()\-–/]+/g, ' ').replace(/\s+/g, ' ').trim();
  return s;
}

// Domain-Root ohne TLD/www/Subdomain: "www.shop.goleasy.de" → "goleasy". Immer vorhanden.
export function domainRoot(lead: DomainLead): string {
  const host = (lead.domain || '').replace(/^www\./i, '').toLowerCase();
  const root = host.replace(_TLD_RE, '');
  const parts = root.split('.').filter(Boolean);
  return parts.length ? parts[parts.length - 1] : root;
}

// Markentoken für LinkedIn/Google-X-Ray-Queries (NICHT NorthData): aufgelöste Mutterfirma
// (ohne Rechtsform) bevorzugt; sonst der gestrippte Impressum-Name, wenn er spezifisch genug ist;
// sonst der immer vorhandene Domain-Root. LinkedIn führt Firmen unter der Marke ohne Rechtsform,
// daher liefert die exakte Phrasensuche mit "… GmbH" 0 Treffer.
export function brandName(lead: DomainLead): string {
  if (lead.parent_company_name) {
    const p = stripLegalSuffix(lead.parent_company_name);
    return p.length >= 3 ? p : lead.parent_company_name;
  }
  const legal = lead.impressum_json?.company_name ? stripLegalSuffix(lead.impressum_json.company_name) : '';
  const root = domainRoot(lead);
  // Kurzer, spezifischer Marken-Token trifft exakt; ein langer Mehrwort-Name ("Honda Motor Europe")
  // scheitert als Phrase → Domain-Root ("honda"). Über-Strip-Schutz: zu kurz/generisch → auch Root.
  if (legal && legal.length >= 4 && legal.split(/\s+/).length <= 2 && !_GENERIC_BRAND.has(legal.toLowerCase())) return legal;
  return root || legal || subsidiaryName(lead);
}

// Prüft, ob der Impressum-Firmenname plausibel zur Domain gehört (geteiltes Token). Spiegelt
// company_names.name_matches_domain im Backend. Bei false → in der Karte „⚠ ungeprüft" zeigen, weil
// evtl. eine Fremdfirma (Agentur/Hoster/Holding) aus dem Impressum kopiert wurde.
export function companyMatchesDomain(lead: DomainLead): boolean {
  const name = lead.impressum_json?.company_name;
  if (!name || !lead.domain) return true;                 // fehlt was → nicht als Fehler werten
  const label = domainRoot(lead).toLowerCase();
  const dflat = label.replace(/[^a-z0-9]/g, '');
  const nflat = stripLegalSuffix(name).toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '');
  if (!dflat || !nflat) return true;
  if (nflat.includes(dflat) || dflat.includes(nflat)) return true;
  return label.split(/[-_.]/).some((p) => p.length >= 4 && nflat.includes(p));
}

// Firma auf LinkedIn: bereits aufgelöste Company-Page direkt (frei), sonst KOSTENLOSER Google-X-Ray
// über linkedin.com/company · NICHT die LinkedIn-eigene Firmensuche (die hinter der Premium-Schranke liegt).
export function companyLinkedInUrl(lead: DomainLead): string {
  if (lead.linkedin_company_page_url) return lead.linkedin_company_page_url;
  const q = `site:linkedin.com/company "${brandName(lead)}"`;
  return `https://www.google.com/search?q=${encodeURIComponent(q)}`;
}

// Entscheider finden: Google X-Ray über LinkedIn-Profile, gefiltert auf Firma + Ziel-Rollen.
// X-Ray ist in der Praxis treffsicherer als LinkedIns Keyword-Personensuche.
export function peopleXrayUrl(lead: DomainLead, titles?: string[]): string {
  const roles = titles && titles.length ? titles.slice(0, 6) : DEFAULT_ROLES;
  const roleQ = roles.map((t) => `"${t}"`).join(' OR ');
  const q = `site:linkedin.com/in "${brandName(lead)}" (${roleQ})`;
  return `https://www.google.com/search?q=${encodeURIComponent(q)}`;
}

// Eine konkrete Person (aus dem Impressum) direkt auf LinkedIn suchen. Name + Marke ist die
// treffsicherste Query (verifiziert: `"Julian Melzig" "GoLeasy"` findet sofort, mit "GmbH" nicht).
export function personXrayUrl(name: string, lead: DomainLead): string {
  const q = `site:linkedin.com/in "${name}" "${brandName(lead)}"`;
  return `https://www.google.com/search?q=${encodeURIComponent(q)}`;
}

// Anzeigenamen aus einer LinkedIn-/in/-Vanity-URL ableiten (gratis, kein Scrape):
// linkedin.com/in/emre-ozmeral-1a2b → „Emre Ozmeral". LinkedIn hängt an den Slug oft eine
// ID/Hash an (z. B. „-1a2b3c" oder „-12345") → nur rein-alphabetische Tokens behalten.
// Gibt null zurück, wenn kein /in/-Slug oder kein Namens-Token übrig bleibt (z. B. reiner Hash-Slug).
export function nameFromLinkedInUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = url.match(/\/in\/([^/?#]+)/i);
  if (!m) return null;
  let slug = m[1];
  try { slug = decodeURIComponent(slug); } catch { /* Roh-Slug behalten */ }
  // Trailing-ID/Disambiguierungs-Ziffern strippen (LinkedIn hängt sie oft an: „hugo-nyberg-94",
  // „hugonyberg94") · sonst würde der Token als „nicht nur Buchstaben" verworfen und der Name leer.
  slug = slug.replace(/[-_]?\d+$/, '');
  const tokens = slug.split(/[-_]/).filter((t) => /^[a-zA-ZäöüÄÖÜßéèáàóòúùçñ]+$/.test(t));
  if (!tokens.length) return null;
  return tokens.map((t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase()).join(' ');
}

// NorthData-Firmensuche (deutsches Handelsregister) · kostenlos.
export function northDataUrl(lead: DomainLead): string {
  return `https://www.northdata.de/?query=${encodeURIComponent(companyName(lead))}`;
}

// NorthData-Suche für einen konkreten Firmennamen (kostenlos). Für die Tochter-Recherche immer mit
// subsidiaryName(lead) aufrufen, damit man die Tochter (und ihr Netzwerk → Mutter) findet.
export function northDataUrlFor(name: string): string {
  return `https://www.northdata.de/?query=${encodeURIComponent(name)}`;
}
